import { cn } from '@/lib/cn';
import { type JSX, splitProps } from 'solid-js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary:
    'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700',
  ghost:
    'bg-transparent text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  success: 'bg-green-600 text-white hover:bg-green-700',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm rounded-lg',
  md: 'h-10 px-4 text-sm rounded-lg',
  lg: 'h-12 px-6 text-base rounded-lg',
};

/** The Button look as plain classes, for non-Button elements (e.g. the Menu trigger). */
export const buttonClass = (variant: Variant = 'primary', size: Size = 'md'): string =>
  cn(
    'inline-flex items-center justify-center gap-2 font-medium transition-colors',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    VARIANTS[variant],
    SIZES[size],
  );

export const Button = (props: ButtonProps): JSX.Element => {
  const [local, rest] = splitProps(props, [
    'variant',
    'size',
    'loading',
    'class',
    'children',
    'disabled',
  ]);
  return (
    <button
      class={cn(buttonClass(local.variant, local.size), local.class as string | undefined)}
      disabled={local.disabled || local.loading}
      type={rest.type ?? 'button'}
      {...rest}
    >
      {local.loading ? (
        <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {local.children}
    </button>
  );
};
